import { Injectable } from '@angular/core';
import { AppSettings } from 'app/app.settings';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Subject } from 'rxjs';

import GeoJSON from 'ol/format/GeoJSON';
import WKT from 'ol/format/WKT';
import { LineString } from 'ol/geom';

import { ActivityService } from './activity.service';
import { AppinfoService, LbColumnType } from './appinfo.service';
import { ArchiveModeService } from './archive-mode.service';
import { AttrIndexService } from './attr-index.service';
import { CommonRequestService } from './common-request.service';
import { EditService } from './edit.service';
import { FieldMappingService, SchemaRequireType } from './field-mapping.service';
import { GfoService } from './gfo.service';
import { Gto, GtoService } from './gto.service';
import { LayerQcService, QcEditFilter } from './layer-qc.service';
import { LayerService } from './layer.service';
import { MainDataService } from './main-data.service';
import { UserService } from './user.service';
import { ParityValue } from './resolution-qc.service';

export type CenterlineSide = 'left' | 'right';
export type QaRuleCode = 'AOE' | 'ARG' | 'ADN' | 'ADR' | 'ASQ' | 'DAV' | 'DAVL' | 'DLS' | 'DPT' | 'DRG' | 'MSV' | 'RAO' | 'RAOL' | 'RDR';

export interface QaResolverFeed {
	rules: QaRuleCode[];
	feed: {
		[layerCate: string]: {
			id; // global id
			cols: string[];
			metrics?: string[];
			list?: string[]; // related global ids
		}
	};
}
export interface ResoCenterCol {
	prop: string;
	label: string;
	lbType: LbColumnType;
	standardName: string;
	htmlTooltip?: string;
	// domains?: any[];
	// domainPattern?: string;
	// domainRange?;
	require?: SchemaRequireType;
}
interface ResoCenterRecord {
	props: {};
	originalProps: {};
	changedCols: string[];
	cateCode: string;
	geomLength?: number; // used for linestring
	// geomChange?: boolean;
	relatedIdList?: any[];
}
export interface ResoCenterRule {
	code: QaRuleCode;
	isFixed?: boolean;
}

export interface RCRecordItem {
	id;
	isSelectedInTable?: boolean; // is the record selected in table/map
	isCurrent?: boolean;
	props: {
		[prop: string]: any;
	}; // Should Link To records props!!!!!!!
	changedCols: string[]; // Should Link To records props!!!!!!!
	// geomChange?: boolean; // Should Link To records geomCHange!!!!!!!
}
export interface ResoCenterMetricsItem {
	type: ResolutionMetricsType;
}

export type ResolutionMetricsType = 'density';

// export interface ResoCenterEditCellEvent {
// 	changedCols: ResoCenterCol[];
// }

@Injectable({
  providedIn: 'root'
})
export class ResolutionCenterService {

	resoFeedBS: BehaviorSubject<{
		record;
		feeds: QaResolverFeed[];
		recordId;
	}> = new BehaviorSubject({
		record: null,
		feeds: null,
		recordId: null
	});

	fromAddrColCore = 'fromaddr'; // lower case
	toAddrColCore = 'toaddr'; // lower case

	drawAdditionalRcSubject: Subject<{
		recordsInfo: {
			wkt: string;
			id;
		}[]
	}> = new Subject();

	private recordsIndex: {
		[id: string]: ResoCenterRecord
	} = {};

	constructor(
		private toastr: ToastrService,
		private activityService: ActivityService,
		private appinfoService: AppinfoService,
		private archiveModeService: ArchiveModeService,
		private attrIndexService: AttrIndexService,
		private requestService: CommonRequestService,
		private editService: EditService,
		private fieldMappingService: FieldMappingService,
		private gfoService: GfoService,
		private gtoService: GtoService,
		private layerService: LayerService,
		private qcService: LayerQcService,
		private mainDataService: MainDataService,
		private userService: UserService
	) { }
	// These two functions should only be used for commit / discard
		getRecordsIndex() {
			return this.recordsIndex;
		}
		setRecordsIndex(data) {
			this.recordsIndex = data;
		}
	getRecordInfo(id) {
		return this.recordsIndex[id] ? this.recordsIndex[id] : null;
	}
	addRecordInfoToStoredRecords(info, id) {
		if (!info) {
			return;
		}
		this.recordsIndex[id] = info;
	}
	setRecordAttrs(id, prop, val) {
		if (!this.recordsIndex[id]) {
			return;
		}
		this.recordsIndex[id].props[prop] = val;
	}
	setDataOriginAttrs(id, prop, val) {
		if (!this.recordsIndex[id]) {
			return;
		}
		this.recordsIndex[id].originalProps[prop] = val;
	}
	// This need to be done AFTER every record / related record's detail has been obtained
	setRelatedIds(feed: QaResolverFeed) {
		if (feed) {
			const setIds = (ids: any[]) => {
				ids.forEach(id => {
					if (this.recordsIndex[id]) {
						this.recordsIndex[id].relatedIdList = ids;
					}
				});
			}
			Object.keys(feed.feed).forEach(cateCode => {
				const info = feed.feed[cateCode];
				if (info.list) {
					const ids = [info.id];
					info.list.forEach(id => {
						ids.push(id);
					});
					setIds(ids);
				}
			});
		}
	}

	getQaResolverFeeds(layerId: number, globalId: string, qaCodes?: string[]) {
		// layerId = 1944;
		// globalId = '3716ca25-ddd1-4ca9-b1c3-ea5a89d919cb'; // '00002c90-8a77-45c4-9d6f-b5b94296381d';
		return new Promise<QaResolverFeed[]>((resolve, reject) => {
			const url = AppSettings.serviceRootUrl + 'azgiv/rcFeedContentQa';
			const params: any = {
				layerId: layerId,
				globalId: globalId
			};
			if (qaCodes && qaCodes.length) {
				params.qaCodes = JSON.stringify(qaCodes);
			}
			this.requestService.requestGet(url, {
				params: params
			}, (rsp) => {
				let feeds: QaResolverFeed[] = []
				if (rsp.data && rsp.data[0]) {
					feeds = rsp.data;
				}
				resolve(feeds);
			}, async err => reject(err));
		});
	}

	getRecordsDetails(layerCateCode: string, ids: string[]) {
		return new Promise<any>(async (resolve, reject) => {
			try {
				const filteredIds = ids.filter(id => !this.recordsIndex[id]);
				if (!filteredIds.length) {
					resolve([]);
					return;
				}
				const layer = this.activityService.displayedLayerListBS.getValue().layers.find(l => l.category.code === layerCateCode);
				const idCol = layer.category.colPrefix + '_' + this.attrIndexService.globalIdColLower;
				const idFilter = this.gfoService.getFilter(idCol, this.appinfoService.filterOperators.in.name, filteredIds);
				const gfo = this.gfoService.getGfo(this.gfoService.filterLogics.and, [idFilter]);
				const rsp = await this.layerService.getLayerContents(layer.id, gfo);
				if (rsp.data) {
					const geomCol = layer.category.colPrefix + '_' + this.attrIndexService.geomCol;
					const geojsonFormat = new GeoJSON();
					const wktFormat = new WKT();
					rsp.data.forEach(data => {
						let geomLength;
						// store wkt geom
						if (data[geomCol]) {
							const feat = geojsonFormat.readFeature(data[geomCol]);
							const wkt = wktFormat.writeGeometry(feat.getGeometry());
							data[this.attrIndexService.geomColManual] = wkt;
							// Calc geom length for lines
							if (feat.getGeometry().getType() === 'LineString') {
								geomLength = (<LineString>feat.getGeometry()).getLength();
							}
						}
						const rec: ResoCenterRecord = {
							props: data,
							originalProps: JSON.parse(JSON.stringify(data)),
							changedCols: [],
							cateCode: layerCateCode
						};
						if (geomLength || geomLength === 0) {
							rec.geomLength = geomLength;
						}
						this.addRecordInfoToStoredRecords(rec, data[idCol]);
						// this.records[data[idCol]] = rec;
					});
				}
				resolve(rsp.data);
			} catch (err) {
				console.log(err);
				reject(err);
			}
		})
		// return new Promise<any[]>(async (resolve, reject) => {
		// 	try {
		// 		const layerCate = this.appinfoService.layerTypeList.find(i => i.code === layerCateCode);
		// 		const idCol = layerCate.colPrefix + '_' + this.attrIndexService.globalIdColLower;
		// 		const idFilter = this.gfoService.getFilter(idCol, this.appinfoService.filterOperators.in.name, ids);
		// 		const gfo = this.gfoService.getGfo(this.gfoService.filterLogics.and, [idFilter]);
		// 		const rsp: any = await this.requestTableService.tableGet(layerCate.tableName, returnedCols.join(','), gfo, {
		// 			pageNumber: 0
		// 		});
		// 		resolve(rsp.data);
		// 	} catch (err) {
		// 		console.log(err);
		// 		reject(err);
		// 	}
		// })
	}

	getGlobalIdCol(layerCateCode: string) {
		const layerCate = this.appinfoService.layerTypeList.find(i => i.code === layerCateCode);
		return layerCate.colPrefix + '_' + this.attrIndexService.globalIdColLower;
	}
	async transformCols(cols: string[], layerCateCode: string): Promise<ResoCenterCol[]> {
		const layerCate = this.appinfoService.layerTypeList.find(i => i.code === layerCateCode);
		if (!layerCate) {
			return [];
		}
		if (!layerCate.standardCols) {
			const stdSchema = await this.fieldMappingService.getStandardSchema(layerCate.id);
			layerCate.standardCols = stdSchema;
		}
		return cols.map(c => {
			let label = c;
			let lbType: LbColumnType;
			const prop = layerCate.colPrefix + '_' + c.toLowerCase();
			const info = this.appinfoService.columnMap[prop];
			if (info) {
				label = info.heading;
				lbType = info.type;
			}

			let tooltip: string;
			let require: SchemaRequireType;
			// let domains: any[];
			// let domainPattern: string, domainRange: string;
			const standardCol = layerCate.standardCols.find(s => s.colName.toLowerCase() === c.toLowerCase());
			if (standardCol) {
				tooltip = standardCol.htmlTooltip;
				require = standardCol.require;
				// const nenaList = this.editCardService.findNenaDomainValueListByColumn(c);
				// if (nenaList && nenaList.length) {
				// 	domains = nenaList;
				// } else if (standardCol.valueDomainNameNct) {
				// 	// // Hard code for county & cols nested
				// 	// if (standardCol.valueDomainNameNct === 'COUNTY') {
				// 	// 	domains = this.userService.customerInfo.cusCounties;
				// 	// } else if (this.editCardService.nctDomainIndex['COUNTY'].child_nct_names.indexOf(standardCol.valueDomainNameNct.split(':')[0]) > -1) {
				// 	// 	const stateValue = this.userService.customerInfo.givState;
				// 	// 	const countyValue = this.userService.customerInfo.cusCounties[0];
				// 	// 	const stateDomain = (<NctNestDomainListItem[]>this.editCardService.nctDomains['STATE'].list).find(i => i.value === stateValue);
				// 	// 	const countyDomain = stateDomain ? (<NctNestDomainListItem[]>stateDomain.subDomain.list).find(i => i.value === countyValue) : null;
				// 	// 	const currentDomain = countyDomain
				// 	// } else {
				// 		// should be direct under nctDomains (no nested domain)
				// 		domains = this.editCardService.getNctDomainValueList(standardCol.valueDomainNameNct, this.editCardService.nctDomains[standardCol.valueDomainNameNct]);
				// 	// }
				// } else if (standardCol.valuePattern) {
				// 	domainPattern = standardCol.valuePattern;
				// }
				// // else if (standardCol.valueRange) {
				// // 	domainRange = standardCol.valueRange;
				// // }
			}

			return {
				prop: prop,
				label: label || c,
				lbType: lbType,
				standardName: c,
				htmlTooltip: tooltip,
				// domains: domains,
				// domainPattern: domainPattern,
				// domainRange: domainRange,
				require: require
			}
		});
	}
	async transformItemList(resolverFeed: QaResolverFeed, layerCateCode: string) {
		const itemList: RCRecordItem[] = [];
		// Record in list
		const currId = resolverFeed.feed[layerCateCode].id;
		let ids = [currId];
		if (resolverFeed.feed[layerCateCode].list) {
			ids = ids.concat(resolverFeed.feed[layerCateCode].list);
		}
		// const returnedCols = cols.map(i => i.prop);
		await this.getRecordsDetails(layerCateCode, ids);
		const currItem: RCRecordItem = {
			id: currId,
			props: this.recordsIndex[currId].props,
			changedCols: this.recordsIndex[currId].changedCols,
			isCurrent: true,
			isSelectedInTable: true
		}
		itemList.push(currItem);
		if (resolverFeed.feed[layerCateCode].list) {
			resolverFeed.feed[layerCateCode].list.forEach(i => {
				const addr: RCRecordItem = {
					id: i,
					props: this.recordsIndex[i].props,
					// originalProps: rsp[idx] ? JSON.parse(JSON.stringify(rsp[idx])) : {},
					changedCols: this.recordsIndex[i].changedCols
				}
				itemList.push(addr);
			});
		}
		return itemList;
	}
	transformRules(rules: QaRuleCode[]): ResoCenterRule[] {
		if (!rules) {
			return [];
		}
		const filterOptions = this.mainDataService.qcFilterInfo.getValue().filterOptions || [];
		return rules.map(r => {
			const filterOption = filterOptions.find(f => f.value === r);
			return {
				code: r,
				description: filterOption ? filterOption.descriptionLong : ''
			}
		});
	}

	onEditRecord(rec: RCRecordItem, col?: {prop: string}, isGeomChange?: boolean) {
		console.log('onEdit')
		if (col && rec.changedCols.indexOf(col.prop) === -1) {
			rec.changedCols.push(col.prop);
		}
		if (isGeomChange && rec.changedCols.indexOf(this.attrIndexService.geomColManual) === -1) {
			rec.changedCols.push(this.attrIndexService.geomColManual);
			// rec.geomChange = true;
		}
	}

	async commit() {
		const changedRecordsByCateCode: {
			[cateCode: string]: ResoCenterRecord[]
		} = {};
		Object.keys(this.recordsIndex).forEach(id => {
			const cateCode = this.recordsIndex[id].cateCode;
			if (!changedRecordsByCateCode[cateCode]) {
				changedRecordsByCateCode[cateCode] = [];
			}
			changedRecordsByCateCode[cateCode].push(this.recordsIndex[id]);
		})

		let updateGtos: Gto[]; // deleteGto: Gto, newRecordsGto: Gto, ;
		let gtos: Gto[] = [];
		let editFilters: QcEditFilter[] = [];
		Object.keys(changedRecordsByCateCode).forEach(cateCode => {
			const layerType = this.appinfoService.layerTypeList.find(t => t.code === cateCode);
			if (!layerType || !layerType.tableName) {
				console.log('Can not find source table')
				return;
			}
			const sourceTableName = layerType.tableName;
			const prefix = layerType.colPrefix;
			let primKeyCol: string;
			primKeyCol = this.editService.getPrimKeyCol(prefix);
			const idCol = this.getGlobalIdCol(cateCode);
			let changedCols: string[] = [];
			const updateRecords = [];
			const updateRecordsOrigin = [];
			changedRecordsByCateCode[cateCode].forEach(i => {
				if (!i.changedCols.length) {
				// if (!i.changedCols.length && !i.geomChange) {
					return;
				}
				i.changedCols.forEach(c => {
					
					if (changedCols.indexOf(c) === -1) {
						changedCols.push(c);
					}
				});
				updateRecords.push(i.props);
				updateRecordsOrigin.push(this.recordsIndex[i.props[idCol]].originalProps);
			});
	
			const dynamicQcKeyvals = [];
			const colsAll = this.editService.getUpdateColsInSourceTable(sourceTableName, prefix);
			if (updateRecords.length) {
				updateRecords.forEach(r => {
					const id = r[idCol];
					if (dynamicQcKeyvals.indexOf(id) === -1) {
						dynamicQcKeyvals.push(id);
					}
					if (this.recordsIndex[id].relatedIdList) {
						this.recordsIndex[id].relatedIdList.forEach(id => {
							if (dynamicQcKeyvals.indexOf(id) === -1) {
								dynamicQcKeyvals.push(id);
							}
						})
					}
				});
				// Use archive mode for resolution center commits
				const updateGtosInfo = this.editService.getUpdateGtosByRecordsArchive(colsAll, updateRecords, updateRecordsOrigin, sourceTableName, prefix, primKeyCol, this.attrIndexService.geomColManual);
				updateGtos = updateGtosInfo.gtos;
				changedCols = updateGtosInfo.changedCols;
				changedCols = changedCols
				// .map(changedCol => {
				// 	// remove prefix
				// 	return changedCol.substring(4); // TODO
				// })
				.filter(c => c);
				gtos = gtos.concat(updateGtos);
				const layerId = this.layerService.layerListBS.getValue().layers.find(l => l.category.code === cateCode && l.maintainType === 'LIVE').id;
				const editFilter: QcEditFilter = {
					gly_id: layerId,
					cols: changedCols,
					keyvals: dynamicQcKeyvals
				};
				editFilters.push(editFilter);
			}
		});
		if (gtos.length) {
			await this.gtoService.processGto(gtos);
			this.toastr.success('Successfully updated.');
			const qcRsp = await this.qcService.qcLayersDynamic(editFilters); // TODO: check transition in misc
			// if (qcRsp.misc && qcRsp.misc.transition > 0) {
			// 	this.qcService.qcLayers(selectedLayer.id);
			// } else {
				this.activityService.refreshData.next({
					updateLayerList: true, // false,
					updateSchema: true // filter option counts need to be updated // false
				});
			// }
		}
	}

// Metrics
	calcAddressDensity(recordProps: {[prop: string]: any}, geomLength: number, side?: CenterlineSide) {
		let leftDensity: number, rightDensity: number;
		if (geomLength) {
			const getDensity = (from, to) => {				
				const fromNum = from ? Number(from) : (from === 0 ? from : null);
				const toNum = to ? Number(to) : (to === 0 ? to : null);
				if (fromNum === null || toNum === null || !geomLength) {
					return null;
				}
				// return Number(((toNum - fromNum) / geomLength).toFixed(2));
				return Number((Math.abs(fromNum - toNum)/(geomLength + 1) / 0.000621371).toFixed(1));

			}
			if (!side || side === 'left') {
				leftDensity = getDensity(recordProps[this.attrIndexService.fromAddrLeftColName], recordProps[this.attrIndexService.toAddrLeftColName]);
			}
			if (!side || side === 'right') {
				rightDensity = getDensity(recordProps[this.attrIndexService.fromAddrRightColName], recordProps[this.attrIndexService.toAddrRightColName]);
			}
		}
		return {
			left: leftDensity,
			right: rightDensity
		}
	}

	getDeltaValueByParity = (currentValue, parity: ParityValue, direction: 'increase' | 'decrease') => {
		if (parity !== 'E' && parity !== 'O') {
			return 1;
		}
		const valueParity = currentValue % 2 === 0 ? 'E' : 'O'
		let delta = valueParity === parity ? 2 : 1; 
		if (direction === 'decrease') {
			delta = delta * -1;
		}
		return delta;
	}
			// getJointChangeMeasure(lineIdx: number, side: CenterlineSide, cols: {fromLeft: string; fromRight: string; toLeft: string; toRight: string; parityLeft: string; parityRight: string;}) {
			// 	let direction: 'increase' | 'decrease';
			// 	let fromColName: string, toColName: string, parityColName: string;
			// 	if (side === 'left') {
			// 		fromColName = cols.fromLeft;
			// 		toColName = cols.toLeft;
			// 		parityColName = cols.parityLeft;
			// 	} else {
			// 		fromColName = cols.fromRight;
			// 		toColName = cols.toRight;
			// 		parityColName = cols.parityRight;
			// 	}
			// 	// Changed fields for first line and second line
			// 	let firstCol: string, secondCol: string;
			// 	if (lineIdx === 0) {
			// 		if (line.direction === 'E' || line.direction === 'S') {
			// 			firstCol = toColName;
			// 			secondCol = fromColName;
			// 			direction = 'increase';
			// 		} else {
			// 			firstCol = fromColName;
			// 			secondCol = toColName;
			// 			direction = 'decrease';
			// 		}
			// 	} else if (lineIdx === 1) {
			// 		if (line.direction === 'E' || line.direction === 'S') {
			// 			firstCol = toColName;
			// 			secondCol = fromColName;
			// 			direction = 'decrease';
			// 		} else {
			// 			firstCol = fromColName;
			// 			secondCol = toColName;
			// 			direction = 'increase';
			// 		}
			// 	} else {
			// 		return;
			// 	}
			// }

}
